import { secAdvApi } from '../../api/sec-adv-api';
import type { ISecAdvFirmCounts, ISecAdvFirmQuery } from '../../api/sec-adv-contracts';
import { useAdminData } from '../../components/admin-page';
import type { SignalSpec } from '../../components/state-cards';

/**
 * The firm list's cards and their signals (SR-423) — a work-item-backed work page's, so each is a
 * parameter of the route survey-api reads: the card as `card=`, the signals pressed as `signal=`
 * repeated, a firm kept where it raises any of them. The keys are tidy constants, never a data
 * value (the work pages' rule 8).
 */
type FirmCard = 'all' | 'registered' | 'era' | 'ours';

interface IFirmCardSpec {
  key: FirmCard;
  label: string;
  /** The label where the page is too narrow for the cards' one row — its whole in the tooltip. */
  short?: string;
  /** A few words on the card. */
  hint: string;
  /** What it holds — the "?"'s words. */
  about: string;
}

export const FIRM_CARDS: readonly IFirmCardSpec[] = [
  {
    key: 'all',
    label: 'All firms',
    hint: 'the whole register',
    about: 'every firm in the register, registered or exempt.'
  },
  {
    key: 'registered',
    label: 'SEC-registered',
    hint: 'registered advisers',
    about: 'the investment advisers registered with the SEC.'
  },
  {
    key: 'era',
    label: 'Exempt reporting advisers',
    short: 'ERA',
    hint: 'report, not registered',
    about: 'the advisers exempt from registration who still file a Form ADV; they report no AUM.'
  },
  {
    key: 'ours',
    label: 'Our firms',
    hint: 'a participant holds it',
    about: 'the firms whose SEC number or CRD a participant holds.'
  }
];

/** The URL's card — one this page does not know is All, and is not sent. */
export function cardOf(raw: Nullable<string>): FirmCard {
  return FIRM_CARDS.find((card) => card.key === raw)?.key ?? 'all';
}

interface IFirmSignal extends SignalSpec {
  /** The filter line's words — "has disclosures". */
  words: string;
  /** What it counts — the "?"'s words. */
  about: string;
  /** A compact card's badge, where `label` is too long for the cards to fit one row. */
  short?: { label: string; tip: string };
}

export const FIRM_SIGNALS: readonly IFirmSignal[] = [
  {
    key: 'filed_30d',
    label: 'filed in 30 days',
    words: 'filed in 30 days',
    short: { label: 'new filings', tip: 'Filed in the last 30 days' },
    tone: 'info',
    about: 'the latest ADV filing is from the last 30 days'
  },
  {
    key: 'stale_filing',
    label: 'stale filing',
    words: 'stale filing',
    tone: 'attention',
    about: 'the latest filing is older than 13 months, or there is none'
  },
  {
    key: 'disclosures',
    label: 'with disclosures',
    words: 'has disclosures',
    tone: 'attention',
    about: 'any Item 11 disclosure question is answered Yes'
  },
  {
    key: 'aum_1b',
    label: 'AUM ≥ $1B',
    words: 'AUM ≥ $1B',
    tone: 'info',
    about: 'total AUM, 5.F(2)(c), is $1 billion or more'
  },
  {
    key: 'survey_submitted',
    label: 'survey submitted',
    words: 'survey submitted',
    tone: 'success',
    about: 'the RIA firm survey of the current cycle is submitted'
  },
  {
    key: 'survey_in_progress',
    label: 'survey in progress',
    words: 'survey in progress',
    tone: 'info',
    about: 'the current cycle’s RIA firm survey is started, not submitted'
  },
  {
    key: 'survey_not_started',
    label: 'survey not started',
    words: 'survey not started',
    tone: 'attention',
    about: 'no response to the current cycle’s RIA firm survey'
  }
];

/** The survey signals, which only Our firms shows: a firm no participant holds has no survey. */
const SURVEY_SIGNALS: readonly string[] = ['survey_not_started', 'survey_in_progress', 'survey_submitted'];

/** The signals a card shows, in its order — Our firms' survey signals first, so its compact card
 *  shows the survey's state. */
export function signalsOfCard(card: FirmCard): readonly IFirmSignal[] {
  const register = FIRM_SIGNALS.filter((signal) => !SURVEY_SIGNALS.includes(signal.key));
  if (card !== 'ours') return register;
  const survey = SURVEY_SIGNALS.flatMap((key) => FIRM_SIGNALS.filter((signal) => signal.key === key));
  return [...survey, ...register];
}

/** The URL's signals, once each, as spelled — one this page does not know is survey-api's 400. */
export function signalsOf(params: URLSearchParams): string[] {
  return [...new Set(params.getAll('signal').filter((signal) => signal !== ''))];
}

/** A signal's words, or its key as spelled. */
export function signalWords(key: string): string {
  return FIRM_SIGNALS.find((signal) => signal.key === key)?.words ?? key;
}

/**
 * Every card's count and its signals' under the list's search and filters, never its card or
 * signals — so a press moves no number (rule 2). Read again when a filter or the search changes,
 * and by `reload` (*Refresh*); never per page. A failed read is no counts: the cards draw without
 * signals (rule 6).
 */
export function useFirmCounts(query: ISecAdvFirmQuery) {
  const filters: ISecAdvFirmQuery = {
    ...query,
    card: undefined,
    signal: undefined,
    sort: undefined,
    dir: undefined,
    columns: undefined,
    page: undefined,
    pageSize: undefined
  };
  const asked = JSON.stringify(filters);
  const read = useAdminData<ISecAdvFirmCounts>((signal) => secAdvApi.firmCounts(filters, signal), [asked]);
  return { counts: read.error ? null : read.data, reading: read.reading, reload: read.reload };
}
